import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

interface UseInactivityOptions {
  /**
   * Duration of inactivity in milliseconds before callback is triggered
   */
  duration: number;
  /**
   * Callback function to execute when user becomes inactive
   */
  onInactive: () => void;
  /**
   * Optional callback when user becomes active again
   */
  onActive?: () => void;
  /**
   * Events to listen for activity (default: ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'])
   */
  events?: ReadonlyArray<keyof WindowEventMap>;
  /**
   * Whether the hook is enabled (default: true)
   */
  enabled?: boolean;
}

/**
 * Hook to detect user inactivity and trigger callbacks
 * @param options Configuration for inactivity detection
 */
export function useInactivity({
  duration,
  onInactive,
  onActive,
  events = DEFAULT_ACTIVITY_EVENTS,
  enabled = true,
}: UseInactivityOptions) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInactiveRef = useRef(false);

  const clearInactivityTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    // Clear existing timeout
    clearInactivityTimeout();

    // If was inactive and now active, trigger onActive callback
    if (isInactiveRef.current && onActive) {
      onActive();
      isInactiveRef.current = false;
    }

    // Set new timeout
    if (enabled) {
      timeoutRef.current = setTimeout(() => {
        isInactiveRef.current = true;
        onInactive();
      }, duration);
    }
  }, [duration, onInactive, onActive, enabled, clearInactivityTimeout]);

  useEffect(() => {
    if (!enabled) {
      clearInactivityTimeout();
      return;
    }

    // Start the timer immediately
    resetInactivityTimer();

    // Add event listeners for activity detection
    const handleActivity = () => {
      resetInactivityTimer();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      clearInactivityTimeout();
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [events, enabled, resetInactivityTimer, clearInactivityTimeout]);

  // Return manual controls
  return {
    /**
     * Manually reset the inactivity timer
     */
    reset: resetInactivityTimer,
    /**
     * Clear the timer without triggering callbacks
     */
    clear: clearInactivityTimeout,
    /**
     * Current inactive state
     */
    isInactive: isInactiveRef.current,
  };
}
